import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getTraineeLiveClasses, generateTraineeJoinToken } from '../../../services/userService';
import Loader from '../../../components/common/Loader';
import NoData from '../../../components/common/NoData';
import { convertUTCToTimezone } from '../../../utils/timeZoneUtils';
import { FormattedTime } from '../../../utils/FormattedTime';
import { initializeSocket } from '../../../utils/socketUtils';

function LiveClass({ classroom_id, classroomName }) {
    const [liveClasses, setLiveClasses] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [joiningMeeting, setJoiningMeeting] = useState(null);
    
    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Summary state
    const [summary, setSummary] = useState({
        live_count: 0,
        scheduled_count: 0,
        ended_count: 0,
        unattended_count: 0,
        total_count: 0
    });

    // Debounce search query to avoid too many API calls
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        if (classroom_id) {
            fetchLiveClasses();
            
            // Initialize socket for live class notifications
            const token = localStorage.getItem('token');
            if (token) {
                initializeSocket(token);
            }
        }
    }, [classroom_id, currentPage, itemsPerPage, debouncedSearchQuery, statusFilter]);

    const fetchLiveClasses = async () => {
        try {
            setLoading(true);
            const response = await getTraineeLiveClasses({ 
                classroom_id,
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearchQuery,
                status: statusFilter
            });
            
            if (response.data) {
                const newLiveClasses = response.data.live_classes || response.data || [];
                
                setLiveClasses(newLiveClasses);
                
                setTotalPages(response.data.pagination?.totalPages || 1);
                setTotalRecords(response.data.pagination?.totalRecords || 0);
                
                // Update summary from API response
                if (response.data.summary) {
                    setSummary(response.data.summary);
                }
            }
        } catch (error) {
            console.error('Error fetching live classes:', error);
            toast.error('Failed to fetch live classes');
        } finally {
            setLoading(false);
        }
    };

    const handleJoinMeeting = async (liveClass) => {
        try {
            setJoiningMeeting(liveClass.id);
const protocol = window.location.protocol;
const hostname = window.location.hostname;
const port = window.location.port;
const fullURL = `${protocol}//${hostname}${port ? `:${port}` : ''}`;    

            const response = await generateTraineeJoinToken({ live_class_id: liveClass.id , sub_domain : fullURL   });

            // Open the join URL in the same tab
            window.location.href = response.data.join_url;
            toast.success('Joining meeting...');
        } catch (error) {
            console.error('Error joining meeting:', error);
            toast.error('Failed to join meeting');
        } finally {
            setJoiningMeeting(null);
        }
    };

    const getStatusBadge = (liveClass) => {
        const base = 'font-semibold px-2.5 py-1 rounded-lg text-xs';
        const status = (liveClass?.status || '').toString().toLowerCase();
        
        // Check if ended (highest priority)
        if (liveClass.is_ended === 1 || status === 'ended') {
            return <span className={`${base} bg-red-50 text-red-600 border border-red-200`}>Ended</span>;
        }
        
        // Check if live
        if (liveClass.is_started === 1 || status === 'started') {
            return <span className={`${base} bg-emerald-50 text-emerald-600 border border-emerald-200`}>Live</span>;
        }
        
        // Check if unattended (start time has passed but never started)
        if (liveClass.start_time) {
            const startTime = new Date(liveClass.start_time);
            const now = new Date();
            
            // If start time has passed, check if duration has also expired
            if (startTime < now) {
                const isNotStartedOrEnded = !(liveClass.is_started === 1) && !(liveClass.is_ended === 1);
                if (isNotStartedOrEnded) {
                    if (liveClass.duration) {
                        const match = liveClass.duration.toString().match(/\d+/);
                        if (match) {
                            const durationMinutes = parseInt(match[0], 10);
                            const endTime = new Date(startTime.getTime() + durationMinutes * 60000);
                            if (endTime < now) {
                                return <span className={`${base} bg-amber-50 text-amber-600 border border-amber-200`}>Unattended</span>;
                            }
                        }
                    } else {
                        // No duration, treat as unattended once the scheduled start time has passed
                        return <span className={`${base} bg-amber-50 text-amber-600 border border-amber-200`}>Unattended</span>;
                    }
                }
            }
        }
        
        return <span className={`${base} bg-[#7083FF]/10 text-[#7083FF] border border-[#7083FF]/30`}>Scheduled</span>;
    };

  

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1); // Reset to first page when search changes
    };

    const clearFilters = () => {
        setSearchQuery('');
        setStatusFilter('');
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleLimitChange = (e) => {
        setItemsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const isUpcoming = (startTime) => {
        return new Date(startTime) > new Date();
    };

    return (
        <div className="overflow-hidden">
            <div className="flex flex-nowrap items-center gap-3 px-0 py-0 border-b-0 bg-transparent overflow-x-auto whitespace-nowrap">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="search"
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF]"
                        placeholder="Search live classes..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        aria-label="Search live classes"
                    />
                </div>
                <select
                    className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 min-w-[140px]"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    aria-label="Filter by status"
                >
                    <option value="">All status</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="live">Live</option>
                    <option value="ended">Ended</option>
                </select>
                {(searchQuery || statusFilter) && (
                    <button
                        type="button"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50"
                        onClick={clearFilters}
                        title="Clear filters"
                    >
                        <Icon icon="mdi:close" width="18" height="18" />
                    </button>
                )}

                {summary && (
                    <div className="flex items-center justify-end gap-1.5 ml-auto">
                        <span className="text-sm text-gray-500 mr-1">
                            {totalRecords} live class{totalRecords !== 1 ? 'es' : ''}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#7083FF]/10 text-[#7083FF] text-[11px] font-semibold leading-none">
                            Live: {summary?.live_count || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 text-[11px] font-semibold leading-none">
                            Upcoming: {summary?.scheduled_count || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-600 text-[11px] font-semibold leading-none">
                            Ended: {summary?.ended_count || 0}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-[11px] font-semibold leading-none">
                            Unattended: {summary?.unattended_count || 0}
                        </span>
                    </div>
                )}
            </div>

            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto mt-4">
                {loading ? (
                    <div className="flex items-center justify-center min-h-[320px]">
                        <Loader caption="Loading live classes..." />
                    </div>
                ) : (
                    <table className="w-full text-sm" style={{ minWidth: '800px' }}>
                        <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">#</th>
                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Class name</th>
                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider max-w-[200px]">Description</th>
                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Date & time</th>
                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">Created by</th>
                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Status</th>
                                <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-36">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {liveClasses.length > 0 ? liveClasses.map((liveClass, index) => {
                                const status = (liveClass?.status || '').toString().toLowerCase();
                                const isEnded = liveClass.is_ended === 1 || status === 'ended';
                                const isStarted = liveClass.is_started === 1 || status === 'started';
                                return (
                                    <tr key={liveClass.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 align-middle text-gray-600">{((currentPage - 1) * itemsPerPage) + index + 1}</td>
                                        <td className="px-4 py-3 align-middle font-medium text-gray-900">{liveClass.live_class_name || liveClass.class_name || '—'}</td>
                                        <td
                                            className="px-4 py-3 align-middle text-gray-500 max-w-[260px]"
                                            title={liveClass.live_class_description || liveClass.class_desc || ''}
                                        >
                                            <div className="line-clamp-2 leading-snug">
                                                {liveClass.live_class_description || liveClass.class_desc || '—'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-middle text-gray-600">
                                            <span className="inline-flex items-center gap-1.5">
                                                <Icon icon="fluent:calendar-24-regular" width="16" height="16" className="text-gray-400" />
                                                {FormattedTime(liveClass.start_time)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 align-middle text-gray-600">{liveClass.created_by_name || '—'}</td>
                                        <td className="px-4 py-3 align-middle">{getStatusBadge(liveClass)}</td>
                                        <td className="px-4 py-3 align-middle">
                                            <div className="flex items-center justify-center gap-2 flex-wrap">
                                                {isEnded ? (
                                                    <span className="text-gray-500 text-sm flex items-center">Ended</span>
                                                ) : isStarted ? (
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-green-500 text-green-500 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        onClick={() => handleJoinMeeting(liveClass)}
                                                        disabled={joiningMeeting === liveClass.id}
                                                        title="Join Meeting"
                                                    >
                                                        {joiningMeeting === liveClass.id ? (
                                                            <span className="inline-block w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <Icon icon="mdi:video" width="18" height="18" />
                                                        )}
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-500 text-sm flex items-center gap-1.5">
                                                        <Icon icon="mdi:clock-outline" width="16" height="16" />
                                                        Not started
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan={7} className="px-4 py-16 text-center">
                                        <NoData
                                            title="No Live Classes Found"
                                            description={searchQuery || statusFilter ? 'No classes match your filters.' : 'No live classes for this classroom yet.'}
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {!loading && (
                <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 px-0 pb-0">
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="hidden sm:inline">Rows per page:</span>
                        <select
                            className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 text-sm"
                            value={itemsPerPage}
                            onChange={handleLimitChange}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="text-gray-500">
                            {totalRecords === 0 ? 'No records' : `Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalRecords)} of ${totalRecords}`}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            type="button"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || totalPages === 0}
                            title="Previous page"
                        >
                            <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
                        </button>
                        <span className="px-2 text-gray-700">
                            Page <span className="font-medium">{totalPages === 0 ? 0 : currentPage}</span> of <span className="font-medium">{totalPages || 0}</span>
                        </span>
                        <button
                            type="button"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            title="Next page"
                        >
                            <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LiveClass;
